import React, { useState, useEffect } from 'react';
import { 
  FaProjectDiagram,
  FaCode,
  FaEye,
  FaExternalLinkAlt,
  FaApple,
  FaAndroid,
  FaGlobe,
  FaPlay,
  FaStore,
  FaTimes
} from 'react-icons/fa';

const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showProjectDetailModal, setShowProjectDetailModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectProducts, setProjectProducts] = useState([]);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const projectsRes = await fetch(`/api/dashboard/admin/projects?page=${currentPage}&limit=10`, { headers });
      if (projectsRes.ok) {
        const projectsData = await projectsRes.json();
        setProjects(projectsData.projects);
        setTotalPages(projectsData.totalPages);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [currentPage]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleViewProjectDetail = async (project) => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Fetch project products
      const productsRes = await fetch(`/api/dashboard/admin/projects/${project._id}/products`, { headers });
      if (productsRes.ok) {
        const productsData = await productsRes.json();
        setProjectProducts(productsData.products || []);
      } else {
        console.error('Products fetch failed:', productsRes.status, productsRes.statusText);
        setProjectProducts([]);
      }

      setSelectedProject(project);
      setShowProjectDetailModal(true);
    } catch (error) {
      console.error('Error fetching project details:', error);
      setProjectProducts([]);
      setSelectedProject(project);
      setShowProjectDetailModal(true);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-gray-400">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-gray-800 border border-gray-700 rounded-xl">
        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Proje</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Kullanıcı</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Şirket</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Ürünler</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Durum</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Oluşturulma</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {projects.map((project) => (
                  <tr key={project._id} className="hover:bg-gray-700/50 transition-colors">
                    <td className="px-6 py-5 whitespace-nowrap">
                      <div className="flex items-center">
                        {project.logo ? (
                          <img 
                            src={project.logo} 
                            alt={project.name}
                            className="h-10 w-10 rounded-lg object-cover mr-4"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'block';
                            }}
                          />
                        ) : null}
                        <FaProjectDiagram 
                          className={`h-6 w-6 text-indigo-400 mr-4 ${project.logo ? 'hidden' : ''}`} 
                        />
                        <div className="text-sm font-medium text-white">{project.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap">
                      <div className="text-sm text-white font-medium">
                        {project.userId?.firstName} {project.userId?.lastName}
                      </div>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap">
                      <div className="text-sm text-white font-medium">{project.companyName}</div>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap">
                      <div className="flex items-center">
                        <FaCode className="h-5 w-5 text-cyan-400 mr-2" />
                        <span className="text-sm font-medium text-white">{project.productCount || 0}</span>
                      </div>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap">
                      <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                        project.status === 'active' 
                          ? 'text-green-400 bg-green-900/20 border border-green-500/30'
                          : project.status === 'inactive'
                          ? 'text-red-400 bg-red-900/20 border border-red-500/30'
                          : 'text-gray-400 bg-gray-900/20 border border-gray-500/30'
                      }`}>
                        {project.status === 'active' ? 'Aktif' : project.status === 'inactive' ? 'Pasif' : 'Arşivlenmiş'}
                      </span>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-400">
                      {formatDate(project.createdAt)}
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => handleViewProjectDetail(project)}
                          className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 rounded-lg transition-colors"
                          title="Detayları Görüntüle"
                        >
                          <FaEye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => window.open(`/project/${project._id}`, '_blank')}
                          className="p-2 text-green-400 hover:text-green-300 hover:bg-green-900/20 rounded-lg transition-colors"
                          title="Projeyi Aç"
                        >
                          <FaExternalLinkAlt className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {projects.length === 0 && (
            <div className="text-center py-12">
              <FaProjectDiagram className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-300">Proje bulunamadı</h3>
              <p className="mt-1 text-sm text-gray-500">Henüz hiç proje oluşturulmamış.</p>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-lg text-gray-300 bg-gray-700 hover:bg-gray-600 disabled:opacity-50"
            >
              Önceki
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-lg text-gray-300 bg-gray-700 hover:bg-gray-600 disabled:opacity-50"
            >
              Sonraki
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-400">
                Sayfa <span className="font-medium text-white">{currentPage}</span> /{' '}
                <span className="font-medium text-white">{totalPages}</span>
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-lg shadow-sm -space-x-px">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-lg border border-gray-600 bg-gray-700 text-sm font-medium text-gray-300 hover:bg-gray-600 disabled:opacity-50"
                >
                  Önceki
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-lg border border-gray-600 bg-gray-700 text-sm font-medium text-gray-300 hover:bg-gray-600 disabled:opacity-50"
                >
                  Sonraki
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Project Detail Modal */}
      {showProjectDetailModal && selectedProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-start justify-between">
                <div className="flex items-center">
                  {selectedProject.logo ? (
                    <img
                      src={selectedProject.logo}
                      alt={selectedProject.name}
                      className="h-12 w-12 rounded-lg object-cover mr-4"
                    />
                  ) : (
                    <FaProjectDiagram className="h-12 w-12 text-indigo-400 mr-4" />
                  )}
                  <div className="text-left">
                    <h2 className="text-2xl font-bold text-white">{selectedProject.name}</h2>
                    <p className="text-gray-400">{selectedProject.companyName}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowProjectDetailModal(false)}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <FaTimes className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Project Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-700 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-white mb-4">Proje Bilgileri</h3>
                  <div className="space-y-3">
                    <div>
                      <span className="text-gray-400 text-sm">Açıklama:</span>
                      <p className="text-white">{selectedProject.description || 'Açıklama yok'}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Sektör:</span>
                      <p className="text-white">{selectedProject.industry}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Şirket Türü:</span>
                      <p className="text-white">{selectedProject.companyType}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Website:</span>
                      <p className="text-white">{selectedProject.website || 'Website yok'}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Durum:</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ml-2 ${
                        selectedProject.status === 'active' 
                          ? 'text-green-400 bg-green-900/20 border-green-500/30'
                          : selectedProject.status === 'inactive'
                          ? 'text-red-400 bg-red-900/20 border-red-500/30'
                          : 'text-gray-400 bg-gray-900/20 border-gray-500/30'
                      }`}>
                        {selectedProject.status === 'active' ? 'Aktif' : selectedProject.status === 'inactive' ? 'Pasif' : 'Arşivlenmiş'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-700 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-white mb-4">Kullanıcı Bilgileri</h3>
                  <div className="space-y-3">
                    <div>
                      <span className="text-gray-400 text-sm">Ad Soyad:</span>
                      <p className="text-white">{selectedProject.userId?.firstName} {selectedProject.userId?.lastName}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">E-posta:</span>
                      <p className="text-white">{selectedProject.userId?.email}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Oluşturulma:</span>
                      <p className="text-white">{formatDate(selectedProject.createdAt)}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Son Güncelleme:</span>
                      <p className="text-white">{formatDate(selectedProject.updatedAt)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Project Products */}
              <div className="bg-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-white mb-4">Proje Ürünleri ({projectProducts.length})</h3>
                
                {projectProducts.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr className="border-b border-gray-600">
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Ürün</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Versiyon</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Durum</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Oluşturulma</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">İşlemler</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-600">
                        {projectProducts.map((product) => (
                          <tr key={product._id} className="hover:bg-gray-600/50">
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center">
                                {product.appIcon?.url ? (
                                  <img
                                    src={product.appIcon.url}
                                    alt={product.name}
                                    className="h-8 w-8 rounded-lg object-cover mr-3"
                                  />
                                ) : (
                                  <div className="h-8 w-8 rounded-lg bg-gray-600 flex items-center justify-center mr-3">
                                    {product.platform === 'ios' ? (
                                      <FaApple className="h-4 w-4 text-gray-300" />
                                    ) : product.platform === 'android' ? (
                                      <FaAndroid className="h-4 w-4 text-green-400" />
                                    ) : product.platform === 'web' ? (
                                      <FaGlobe className="h-4 w-4 text-blue-400" />
                                    ) : (
                                      <FaCode className="h-4 w-4 text-cyan-400" />
                                    )}
                                  </div>
                                )}
                                <div className="text-left">
                                  <div className="text-sm font-medium text-white">{product.name || 'İsimsiz Ürün'}</div>
                                  {product.description && (
                                    <div className="text-sm text-gray-400 truncate max-w-xs">{product.description}</div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300 text-left">
                              {product.version}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-left">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${
                                product.status === 'published' 
                                  ? 'text-green-400 bg-green-900/20 border-green-500/30'
                                  : product.status === 'development'
                                  ? 'text-yellow-400 bg-yellow-900/20 border-yellow-500/30'
                                  : 'text-gray-400 bg-gray-900/20 border-gray-500/30'
                              }`}>
                                {product.status === 'published' ? 'Yayında' : 
                                 product.status === 'development' ? 'Geliştirme' : 
                                 product.status === 'testing' ? 'Test' : 
                                 product.status === 'suspended' ? 'Askıya Alındı' : 'Arşivlenmiş'}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-400 text-left">
                              {formatDate(product.createdAt)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-left">
                              <div className="flex space-x-2">
                                {product.appStoreLinks?.googlePlay && (
                                  <button
                                    onClick={() => window.open(product.appStoreLinks.googlePlay, '_blank')}
                                    className="text-green-400 hover:text-green-300"
                                    title="Google Play Store"
                                  >
                                    <FaPlay className="h-4 w-4" />
                                  </button>
                                )}
                                {product.appStoreLinks?.appStore && (
                                  <button
                                    onClick={() => window.open(product.appStoreLinks.appStore, '_blank')}
                                    className="text-gray-300 hover:text-white"
                                    title="App Store"
                                  >
                                    <FaStore className="h-4 w-4" />
                                  </button>
                                )}
                                {product.website && (
                                  <button
                                    onClick={() => window.open(product.website, '_blank')}
                                    className="text-blue-400 hover:text-blue-300"
                                    title="Website"
                                  >
                                    <FaExternalLinkAlt className="h-4 w-4" />
                                  </button>
                                )}
                                {!product.appStoreLinks?.googlePlay && !product.appStoreLinks?.appStore && !product.website && (
                                  <span className="text-gray-500 text-xs">URL yok</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FaCode className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-300">Ürün bulunamadı</h3>
                    <p className="mt-1 text-sm text-gray-500">Bu projede henüz hiç ürün oluşturulmamış.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProjects;
